import React, { useState } from "react"
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormControlLabel
} from "@carrotfertility/carotene-core"
import useEmployee from "components/employees/hooks/useEmployee"
import useCarrotPlans from "./hooks/useCarrotPlans"
import useAdminUser from "components/hooks/useAdminUser"
import useDeleteTestCarrotPlan from "./hooks/useDeleteTestCarrotPlan"

interface DeleteTestCarrotPlanDialogProps {
  employeeId: number
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const formatNameFromEmail = (email: string): string => {
  const prefix = email.split("@")[0]
  return prefix
    .split(".")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

export default function DeleteTestCarrotPlanDialog({
  employeeId,
  open,
  onClose,
  onSuccess
}: DeleteTestCarrotPlanDialogProps) {
  const [confirmed, setConfirmed] = useState(false)
  const { data: employeeData } = useEmployee(employeeId.toString())
  const { data: plansList } = useCarrotPlans(employeeId)
  const { data: adminUser } = useAdminUser()
  const {
    mutateAsync: deleteTestCarrotPlan,
    isLoading: isDeleting,
    error: deleteError,
    reset
  } = useDeleteTestCarrotPlan(employeeId)

  const employee = employeeData?.employee

  const handleClose = () => {
    if (isDeleting) return
    reset()
    setConfirmed(false)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmed) return
    try {
      await deleteTestCarrotPlan()
      onSuccess()
      handleClose()
    } catch {
      // error is surfaced via deleteError from useMutation
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: "#e53935" }}>Delete Test Carrot Plan</DialogTitle>
      <DialogContent>
        {deleteError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Something went wrong. Please open a ticket in #eng-support with the employee ID and any error details.
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          {([
            ["Member", `${employee?.preferredName ?? employee?.firstName} ${employee?.lastName}`],
            ["Employee ID", employeeId],
            ["Company", employeeData?.company.name],
            ["Journey", plansList?.[0]?.journeys?.[0]?.journeyDisplayName ?? "—"]
          ] as [string, string | number | undefined][]).map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider"
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: label === "Employee ID" ? "monospace" : undefined,
                  textAlign: "right",
                  ml: 2
                }}
              >
                {value ?? "—"}
              </Typography>
            </Box>
          ))}
        </Box>
        <Alert severity="error" sx={{ mb: 2, bgcolor: "#ffebee" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            This action will do the following:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            <li>All Carrot Plan data will be permanently deleted</li>
            <li>Routing info, settings, and medical info will be removed</li>
            <li>
              A notification including your login info will be posted to <strong>#eng-deploys</strong> for audit
              purposes
            </li>
          </ul>
        </Alert>
        <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: "#fffde7", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Logged in as:{" "}
            <strong>
              {formatNameFromEmail(adminUser?.email ?? "")} ({adminUser?.email})
            </strong>
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{ color: "#e53935", "&.Mui-checked": { color: "#e53935" } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "#e53935", fontWeight: 500 }}>
              I confirm this is a test Carrot Plan and understand this deletion is permanent and cannot be reversed.
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" variant="outlined" onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDelete}
          disabled={!confirmed || isDeleting}
          sx={{
            bgcolor: "#e53935",
            color: "#fff",
            "&:hover": { bgcolor: "#c62828", border: "2px solid #000" },
            border: "2px solid #fff",
            "&.Mui-disabled": { border: "2px solid rgba(255,255,255,0.3)" }
          }}
        >
          {isDeleting ? "Deleting…" : "Delete Carrot Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
